import React, { useState, useEffect, useCallback, useMemo } from "react";
import { displayName, useMaskNames } from "./nameMask";

const API = "https://api.pharmacegroup.co.uk";

/**
 * Invoice analysis — what each branch buys, from whom, and what it costs.
 *
 * Charts are plain HTML and CSS rather than recharts, following the precedent
 * set in DeliveryActivity.js: ResponsiveContainer renders empty inside this
 * layout, and a chart that silently shows nothing is worse than a plain one
 * that works.
 *
 * The normalised view is the one that answers the real question. Comparing
 * branch spend directly says little, because a bigger branch spends more by
 * definition; spend per 1,000 items dispensed is what makes two branches
 * comparable. Where a branch has not recorded its item counts the normalised
 * figure is left blank rather than estimated — an invented denominator
 * produces a confident, wrong comparison, which is worse than a gap.
 */
const InvoiceAnalysis = () => {
  const masked = useMaskNames();

  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [drugs, setDrugs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState("spend");      // spend | normalised | suppliers | drugs
  const [branchFilter, setBranchFilter] = useState("");
  const [drill, setDrill] = useState(null);       // { pharmacyId, month }

  const token = localStorage.getItem("authToken");

  const post = useCallback(async (path, body = {}) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
    return j;
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await post("/api/invoiceAnalysis/summary", { year }));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [post, year]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (view !== "drugs" || drugs?.year === year) return;
    post("/api/invoiceAnalysis/drugs", { year })
      .then(setDrugs).catch((e) => setError(e.message));
  }, [view, year, drugs, post]);

  const money = (p) => (p == null ? "—" : `£${(p / 100).toFixed(2)}`);
  const moneyShort = (p) =>
    p == null ? "—"
    : p >= 100000 ? `£${(p / 100000).toFixed(1)}k`
    : `£${Math.round(p / 100)}`;

  const shown = useMemo(
    () => (data?.branches || []).filter((b) => !branchFilter || b.pharmacyId === branchFilter),
    [data, branchFilter]
  );

  const S = {
    card: { border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", marginBottom: 14 },
    head: { padding: "8px 11px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
            fontWeight: "bold", fontSize: 13 },
    th: { textAlign: "left", padding: "6px 8px", fontSize: 11.5, color: "#475569",
          whiteSpace: "nowrap" },
    td: { padding: "6px 8px", fontSize: 12.5 },
    btn: (on) => ({ padding: "5px 11px", marginRight: 6, cursor: "pointer",
                    border: "1px solid #cbd5e1", borderRadius: 4,
                    background: on ? "#1d4ed8" : "#fff",
                    color: on ? "#fff" : "#0f172a", fontSize: 13 }),
  };

  /** A month-by-month bar row. Scale is per row so a small branch is readable. */
  const BarRow = ({ series, valueOf, label, formatter }) => {
    const max = Math.max(...series.map((s) => valueOf(s) || 0), 1);
    return (
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 76 }}>
        {series.map((s, i) => {
          const v = valueOf(s);
          const h = v ? Math.max(2, (v / max) * 62) : 0;
          return (
            <div key={i} style={{ flex: 1, textAlign: "center" }}
                 title={`${s.month}: ${formatter(v)}`}>
              <div style={{ height: 62, display: "flex", alignItems: "flex-end" }}>
                <div style={{
                  width: "100%", height: h,
                  background: v == null ? "#e2e8f0" : "#1d4ed8",
                  borderRadius: "2px 2px 0 0",
                  cursor: v ? "pointer" : "default",
                }} onClick={() => v && setDrill({ pharmacyId: label, month: s.month })} />
              </div>
              <div style={{ fontSize: 9, color: "#94a3b8" }}>{s.month.slice(5)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                    marginBottom: 12 }}>
        <select value={year} onChange={(e) => setYear(+e.target.value)}
                style={{ padding: 6 }}>
          {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 4 + i)
            .map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}
                style={{ padding: 6, minWidth: 190 }}>
          <option value="">All branches</option>
          {(data?.branches || []).map((b) => (
            <option key={b.pharmacyId} value={b.pharmacyId}>
              {displayName(b.pharmacyName, masked)}
            </option>
          ))}
        </select>

        <span>
          {[["spend", "Spend"], ["normalised", "Per 1,000 items"],
            ["suppliers", "By supplier"], ["drugs", "By drug"]].map(([k, l]) => (
            <button key={k} style={S.btn(view === k)} onClick={() => setView(k)}>{l}</button>
          ))}
        </span>

        {data && (
          <span style={{ marginLeft: "auto", fontSize: 12.5, color: "#475569" }}>
            {money(data.totals.netPence)} across {data.totals.invoiceCount} invoice
            {data.totals.invoiceCount === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {loading && <div style={{ padding: 14 }}>Loading…</div>}
      {error && (
        <div style={{ background: "#fff4f4", border: "1px solid #e0b4b4",
                      padding: "8px 10px", marginBottom: 10 }}>{error}</div>
      )}

      {data && !loading && data.branches.length === 0 && (
        <div style={{ ...S.card, padding: 18, color: "#64748b" }}>
          No invoices with values for {year}. Spend is taken from the supplier's
          own line totals, so an invoice that has not been parsed yet contributes
          nothing here.
        </div>
      )}

      {/* ------------------------------------------------------- spend --- */}
      {data && !loading && (view === "spend" || view === "normalised") &&
        shown.map((b) => (
        <div key={b.pharmacyId} style={S.card}>
          <div style={{ ...S.head, display: "flex", justifyContent: "space-between" }}>
            <span>{displayName(b.pharmacyName, masked)}</span>
            <span style={{ fontWeight: "normal", color: "#475569" }}>
              {view === "normalised"
                ? (b.perThousandPence != null
                    ? `${money(b.perThousandPence)} per 1,000 items`
                    : "item counts not recorded — cannot normalise")
                : `${money(b.totalPence)} for the year`}
            </span>
          </div>
          <div style={{ padding: "10px 11px" }}>
            {view === "normalised" && b.perThousandPence == null ? (
              <div style={{ color: "#a05a00", fontSize: 12.5 }}>
                This branch has not recorded item counts in its Periodic Tasks
                for {year}, so its spend cannot be compared with the others.
                The figure is left out rather than estimated.
              </div>
            ) : (
              <BarRow
                series={b.monthly}
                label={b.pharmacyId}
                valueOf={(m) => view === "normalised" ? m.perThousandPence : m.netPence}
                formatter={(v) => money(v)}
              />
            )}
          </div>
        </div>
      ))}

      {/* --------------------------------------------------- suppliers --- */}
      {data && !loading && view === "suppliers" && (
        <div style={S.card}>
          <div style={S.head}>Spend by supplier and branch</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>Branch</th>
                {(data.suppliers || []).map((s) => (
                  <th key={s.name} style={{ ...S.th, textAlign: "right" }}>{s.name}</th>
                ))}
                <th style={{ ...S.th, textAlign: "right" }}>Total</th>
              </tr></thead>
              <tbody>
                {shown.map((b) => (
                  <tr key={b.pharmacyId} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={S.td}>{displayName(b.pharmacyName, masked)}</td>
                    {(data.suppliers || []).map((s) => {
                      const hit = b.suppliers.find((x) => x.supplierName === s.name);
                      const share = hit && b.totalPence
                        ? hit.netPence / b.totalPence : 0;
                      return (
                        <td key={s.name} style={{
                          ...S.td, textAlign: "right",
                          // shaded by share of that branch's spend, so a branch
                          // buying almost everything from one wholesaler stands out
                          background: share ? `rgba(29,78,216,${(share * 0.35).toFixed(2)})` : undefined,
                        }}>
                          {hit ? money(hit.netPence) : "—"}
                        </td>
                      );
                    })}
                    <td style={{ ...S.td, textAlign: "right", fontWeight: "bold" }}>
                      {money(b.totalPence)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- drugs --- */}
      {view === "drugs" && (
        <div style={S.card}>
          <div style={S.head}>
            Quantities purchased by drug and month
            {drugs && <span style={{ fontWeight: "normal", color: "#64748b" }}>
              {"  "}top {drugs.drugs.length} by value
            </span>}
          </div>
          {!drugs && <div style={{ padding: 12 }}>Loading…</div>}
          {drugs && (
            <div style={{ overflowX: "auto", maxHeight: "62vh" }}>
              <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
                <thead><tr style={{ background: "#fafafa" }}>
                  <th style={{ ...S.th, position: "sticky", left: 0, background: "#fafafa" }}>
                    Drug
                  </th>
                  <th style={S.th}>PIP</th>
                  {drugs.months.map((m) => (
                    <th key={m} style={{ ...S.th, textAlign: "right" }}>{m.slice(5)}</th>
                  ))}
                  <th style={{ ...S.th, textAlign: "right" }}>Packs</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Value</th>
                </tr></thead>
                <tbody>
                  {drugs.drugs
                    .filter((d) => !branchFilter || d.byBranch[branchFilter])
                    .map((d) => (
                    <tr key={d.key} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <td style={{ ...S.td, position: "sticky", left: 0, background: "#fff",
                                   whiteSpace: "nowrap", maxWidth: 280, overflow: "hidden",
                                   textOverflow: "ellipsis" }} title={d.description}>
                        {d.description}
                      </td>
                      <td style={{ ...S.td, color: "#64748b" }}>{d.pip || "—"}</td>
                      {drugs.months.map((m) => (
                        <td key={m} style={{ ...S.td, textAlign: "right",
                                             color: d.byMonth[m] ? "#0f172a" : "#cbd5e1" }}>
                          {d.byMonth[m] || "·"}
                        </td>
                      ))}
                      <td style={{ ...S.td, textAlign: "right", fontWeight: "bold" }}>
                        {d.totalQty}
                      </td>
                      <td style={{ ...S.td, textAlign: "right" }}>{money(d.totalPence)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------- drill --- */}
      {drill && data && (
        <div onClick={() => setDrill(null)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      zIndex: 120 }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: "#fff", borderRadius: 6, width: 620,
                        maxHeight: "78vh", overflowY: "auto", padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>
              {displayName(
                data.branches.find((b) => b.pharmacyId === drill.pharmacyId)?.pharmacyName,
                masked)} — {drill.month}
            </h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#fafafa" }}>
                <th style={S.th}>Supplier</th><th style={S.th}>Invoice</th>
                <th style={S.th}>Date</th>
                <th style={{ ...S.th, textAlign: "right" }}>Lines</th>
                <th style={{ ...S.th, textAlign: "right" }}>Net</th>
              </tr></thead>
              <tbody>
                {data.invoices
                  .filter((i) => i.pharmacyId === drill.pharmacyId && i.month === drill.month)
                  .map((i) => (
                  <tr key={i._id} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={S.td}>{i.supplierName}</td>
                    <td style={S.td}>{i.invoiceNumber || "—"}</td>
                    <td style={S.td}>{(i.date || "").slice(0, 10)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{i.lineCount}</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: "bold" }}>
                      {money(i.netPence)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button style={S.btn(true)} onClick={() => setDrill(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <p style={{ fontSize: "0.82em", color: "#64748b", marginTop: 8 }}>
        Spend is the supplier's own line totals, net of VAT — what was charged,
        not what physically arrived. Item counts for normalisation come from each
        branch's Periodic Task returns.
      </p>
    </div>
  );
};

export default InvoiceAnalysis;
